import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, CircleNotch } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

const STEPS = ["indexing", "planningStructure", "generatingContent"] as const;

const STEP_DELAYS_MS = [4000, 10000] as const;

type TStepStatus = "completed" | "active" | "pending";

function getStepStatus(stepIndex: number, current: number): TStepStatus {
  if (stepIndex < current) return "completed";
  if (stepIndex === current) return "active";
  return "pending";
}

function StepIcon({ status }: { status: TStepStatus }) {
  if (status === "completed") {
    return <CheckIcon size={16} weight="bold" className="text-green-500" />;
  }
  if (status === "active") {
    return <CircleNotch size={16} className="animate-spin text-zinc-400" />;
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-zinc-300">
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

type TGenerationStepsProps = {
  activeStep?: number | null;
};

export function GenerationSteps({ activeStep: externalStep }: TGenerationStepsProps) {
  const { t } = useTranslation("projectHome");
  const useTimers = externalStep === undefined || externalStep === null;
  const [timerStep, setTimerStep] = useState(0);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    if (!useTimers) return;
    let elapsed = 0;
    for (let i = 0; i < STEP_DELAYS_MS.length; i++) {
      elapsed += STEP_DELAYS_MS[i];
      const step = i + 1;
      const timer = setTimeout(() => setTimerStep(step), elapsed);
      timersRef.current.push(timer);
    }
    return () => timersRef.current.forEach(clearTimeout);
  }, [useTimers]);

  const current = useTimers ? timerStep : externalStep;

  return (
    <div className="flex flex-col gap-2.5">
      {STEPS.map((label, i) => {
        const status = getStepStatus(i, current);
        return (
          <div key={label} className="flex items-center gap-2.5">
            <StepIcon status={status} />
            <span
              className={cn(
                "text-[14px] leading-5",
                status === "pending" && "text-zinc-300",
                status === "active" && "text-zinc-800",
                status === "completed" && "text-zinc-800"
              )}
            >
              {t(`presentation.steps.${label}`)}
            </span>
          </div>
        );
      })}
    </div>
  );
}
